import { format, differenceInDays, addDays } from 'date-fns';
import { id as idLocale, enUS } from 'date-fns/locale';

// ─── CURRENCY ─────────────────────────────────────────────
export const formatCurrency = (amount, lang = 'id') => {
  return new Intl.NumberFormat(lang === 'id' ? 'id-ID' : 'en-US', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(amount);
};

// ─── DATE HELPERS ─────────────────────────────────────────
export const formatDate = (date, lang = 'id', fmt = 'dd MMM yyyy') => {
  if (!date) return '';
  return format(new Date(date), fmt, {
    locale: lang === 'id' ? idLocale : enUS,
  });
};

export const calcNights = (checkin, checkout) => {
  if (!checkin || !checkout) return 0;
  return Math.max(0, differenceInDays(new Date(checkout), new Date(checkin)));
};

export const getTomorrowDate = () => addDays(new Date(), 1);
export const getDayAfterTomorrow = () => addDays(new Date(), 2);

// ─── ROOM HELPERS ─────────────────────────────────────────
export const getRoomTypeLabel = (type, lang = 'id') => {
  const labels = {
    id: {
      single: 'Single Room',
      deluxe_twin: 'Deluxe Twin Room',
      large_double: 'Large Double Room',
      bungalow_deluxe: 'Bungalow Deluxe',
      family_suite: 'Deluxe Family Suite',
    },
    en: {
      single: 'Single Room',
      deluxe_twin: 'Deluxe Twin Room',
      large_double: 'Large Double Room',
      bungalow_deluxe: 'Bungalow Deluxe',
      family_suite: 'Deluxe Family Suite',
    },
  };
  return labels[lang][type] || type;
};

// ─── URL / IMAGE HELPERS ──────────────────────────────────
export const getImageUrl = (path) => {
  if (!path) return '/assets/placeholder.jpg';
  
  // Jika path adalah link luar (http), biarkan saja
  if (path.startsWith('http')) return path;
  
  // JIKA path dimulai dengan /images/, itu adalah file lokal, jangan tambahkan URL API
  if (path.startsWith('/images/')) return path;
  
  // Jika bukan file lokal, baru gunakan URL API (untuk gambar dari backend)
  return `${process.env.REACT_APP_API_URL?.replace('/api', '')}/media/${path}`;
};

// ─── CLASS NAME HELPERS ───────────────────────────────────
export const cn = (...classes) => classes.filter(Boolean).join(' ');

// ─── BOOKING STATUS COLORS ───────────────────────────────
export const getBookingStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    checked_in: 'bg-blue-100 text-blue-800',
    checked_out: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// ─── SYNC STATUS HELPERS ──────────────────────────────────
export const getSyncStatusColor = (status) => {
  const colors = {
    synced: 'text-green-500',
    pending: 'text-yellow-500',
    error: 'text-red-500',
    syncing: 'text-blue-500',
  };
  return colors[status] || 'text-gray-500';
};

// --- src/utils/helpers.js ---

// Simulasi Aturan Musim (Bisa kamu sesuaikan dengan data riwayat homestay-mu)
export const getSeasonMultiplier = (date) => {
  const month = date.getMonth(); // 0 = Jan, 11 = Des
  const day = date.getDate();

  // 1. PEAK SEASON: Libur Nataru (20 Des - 5 Jan) -> Harga +50%
  if ((month === 11 && day >= 20) || (month === 0 && day <= 5)) {
    return 1.5; 
  }
  // 2. HIGH SEASON: Libur Sekolah (Juni & Juli) -> Harga +25%
  if (month === 5 || month === 6) {
    return 1.25; 
  }
  // 3. LOW SEASON: Februari & Maret (Sepi) -> Harga Diskon 15%
  if (month === 1 || month === 2) {
    return 0.85; 
  }
  
  // 4. NORMAL SEASON: Bulan sisanya -> Harga Normal
  return 1.0; 
};

// Fungsi menghitung harga 1 malam untuk tanggal tertentu
export const calculateNightlyPrice = (basePrice, date) => {
  return basePrice * getSeasonMultiplier(date);
};

// Fungsi menghitung total harga menginap (karena bisa melewati pergantian musim)
export const calculateTotalStayPrice = (basePrice, checkinDate, checkoutDate) => {
  let total = 0;
  // Buat copy tanggal agar tidak merusak state asli
  let currentDate = new Date(checkinDate);
  currentDate.setHours(0, 0, 0, 0);
  
  let end = new Date(checkoutDate);
  end.setHours(0, 0, 0, 0);

  // Looping per malam
  while (currentDate < end) {
    total += calculateNightlyPrice(basePrice, currentDate);
    currentDate.setDate(currentDate.getDate() + 1); // Tambah 1 hari
  }
  return total;
};